import IndicatorBadge from "@/components/status/IndicatorBadge";
import { getBaseThemeName, getTheme } from "@/features/branding";
import type { AnalysisPointDetailTab, AnalysisResultsTab } from "@/features/maps";
import { PortfolioExportJobSchema } from "@/tools/aggregate/portfolio-export/types";
import { getAnalysisTypeName } from "@/utils/string";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { DotsNineIcon } from "@phosphor-icons/react";
import { Button, Tabs, TabsProps, Tooltip } from "antd";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useCollectionState } from "../context/CollectionContext";
import { useCollectionTutorialsState } from "../context/CollectionTutorialContext";
import { AnalysisMetadata } from "../types";
import styles from "./PointLevelHeaderTabs.module.scss";
type TabItem = NonNullable<TabsProps["items"]>[number];
interface PointLevelHeaderTabsProps {
  schema: PortfolioExportJobSchema;
  canRunAutoAdaptation: boolean;
  analysisSettings: AnalysisMetadata | undefined;
  isAnalysisSettingsLoading: boolean;
}
export const PointTabTitle: Record<AnalysisPointDetailTab, string> = {
  risk: "Direct Risk",
  exposure: "Flood Exposure",
  adaptation: "Resilience",
  metadata: "Metadata"
};
export const PointLevelHeaderTabs = ({
  schema,
  canRunAutoAdaptation,
  analysisSettings,
  isAnalysisSettingsLoading
}: PointLevelHeaderTabsProps) => {
  const {
    activeTab,
    setActiveTab,
    selectedId,
    setSelectedId,
    setHighlightedId,
    selectedDisplayName,
    setSelectedDisplayName
  } = useCollectionState();
  const {
    bindBackToAssetListTrigger,
    riskTabTrigger,
    adaptationTabTrigger
  } = useCollectionTutorialsState();
  const {
    antd
  } = getTheme();
  const colorPrimary = antd.token?.colorPrimary;
  const {
    t
  } = useTranslation("terms");
  const sidebarMode = selectedId ? "detail" : "list";
  const hasAutoAdaptations = schema.autoAdaptationStatus !== null;
  const exposureHidden = !isAnalysisSettingsLoading && !analysisSettings?.exposuresEnabled;

  // keep in case we add more tabs to detail but not point list
  const activeTabKey: AnalysisPointDetailTab = activeTab;
  const handleBack = () => {
    setHighlightedId(null);
    setSelectedId(null);
    setSelectedDisplayName(null);
  };
  const handleTabChange = (key: AnalysisPointDetailTab) => {
    if (key === "exposure" && isAnalysisSettingsLoading) return;
    setActiveTab(key as AnalysisResultsTab);
  };
  const tabItems: TabsProps["items"] = useMemo(() => {
    const TabLabel = ({
      tabKey,
      label,
      hidden = false,
      disabled = false,
      tooltip,
      tabRef
    }: {
      tabKey: string;
      label: string | React.ReactNode;
      hidden?: boolean;
      disabled?: boolean;
      tooltip?: string;
      tabRef?: React.MutableRefObject<null>;
    }) => {
      const isSelected = activeTabKey === tabKey;
      const base = "mt-3 inline-flex  h-full min-h-[34px] leading-none w-full min-w-[200px] items-center rounded-t-lg border !border-b-0 border-gray-300 px-5 py-1 font-semibold tracking-[0.01em] backdrop-blur-sm backdrop-saturate-200 transition-colors";
      const state = isSelected ? "bg-gray-50" : disabled ? "cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400" : "bg-gray-200 border-gray-300 hover:bg-gray-100 ";
      return tooltip ? <Tooltip title={tooltip} data-sentry-element="Tooltip" data-sentry-component="TabLabel" data-sentry-source-file="PointLevelHeaderTabs.tsx">
          <div ref={tabRef} className={`${base} ${state} ${hidden ? "hidden" : ""}`}>
            {label}
          </div>
        </Tooltip> : <div ref={tabRef} className={`${base} ${state} ${hidden ? "hidden" : ""}`} data-sentry-component="TabLabel" data-sentry-source-file="PointLevelHeaderTabs.tsx">
          {label}
        </div>;
    };
    const tabs: TabItem[] = [{
      key: "risk",
      label: <TabLabel tabKey="risk" label={PointTabTitle["risk"]} tabRef={riskTabTrigger} />
    }];
    if (!schema.isLargeSite && (getBaseThemeName() !== "hkma" || getBaseThemeName() === "hkma" && canRunAutoAdaptation) && schema.updateAvailable) {
      const adaptationDisabled = !canRunAutoAdaptation || !hasAutoAdaptations;
      tabs.push({
        key: "adaptation",
        disabled: adaptationDisabled,
        label: <TabLabel tabKey="adaptation" tabRef={adaptationTabTrigger} disabled={adaptationDisabled} tooltip={!canRunAutoAdaptation ? "This feature is not currently available to your organisation. Please contact your Customer Success Manager to have this feature enabled for this account." : !hasAutoAdaptations ? "No Resilience data detected. Re-process this analysis to access updated data." : undefined} label={<IndicatorBadge key={`new-${sidebarMode}-adaptation`} placement="right" offset={[8, 0]} mode="inline">
                {PointTabTitle["adaptation"]}
              </IndicatorBadge>} />
      });
    }
    tabs.push({
      key: "exposure",
      disabled: isAnalysisSettingsLoading,
      label: <TabLabel tabKey="exposure" label={PointTabTitle["exposure"]} hidden={exposureHidden} disabled={isAnalysisSettingsLoading} />
    });
    return tabs;
  }, [activeTabKey, sidebarMode, exposureHidden, isAnalysisSettingsLoading, analysisSettings, canRunAutoAdaptation, hasAutoAdaptations, colorPrimary]);
  return <div className="flex items-center border-b border-gray-300" data-sentry-component="PointLevelHeaderTabs" data-sentry-source-file="PointLevelHeaderTabs.tsx">
      <div className="flex h-6 min-w-0 flex-1 items-center gap-x-1 truncate pl-2 text-gray-900">
        {sidebarMode === "detail" ? <>
            <Button ref={bindBackToAssetListTrigger} key="detail-header-icon" icon={<ArrowLeftOutlined />} onClick={handleBack} type="text" className="animate-fade-in h-[24px] text-gray-800">
              Back to all assets
            </Button>
            <div className="min-w-0 truncate text-sm leading-none">
              <span className="text-gray-400">{t("userSuppliedId")}:</span>
              <span title={selectedDisplayName ?? ""} className="ml-2 font-semibold">
                {selectedDisplayName}
              </span>
            </div>
          </> : <span className="inline-flex h-6 items-center gap-2 rounded-md px-1">
            <DotsNineIcon key="list-header-icon" weight="bold" size={24} className="animate-fade-in" />
            <span>All {getAnalysisTypeName(schema.isLargeSite, true)}</span>
          </span>}
      </div>
      <Tabs items={tabItems} activeKey={activeTabKey} onChange={key => handleTabChange(key as AnalysisPointDetailTab)} rootClassName={`${styles.pointTabs} pr-4`} classNames={{
      header: "!m-0 min-w-[200px]",
      item: "!p-0",
      indicator: "!bg-gray-50 !h-[2px] !-bottom-px"
    }} tabBarGutter={4} data-sentry-element="Tabs" data-sentry-source-file="PointLevelHeaderTabs.tsx" />
    </div>;
};